import React, { useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const TopDrugs = ({ chartData }) => {
    const [selectedLabel, setSelectedLabel] = useState(null); // null = show all
    const [viewMode, setViewMode] = useState("graph"); // "graph" | "patients"

    // Filter data based on selected label
    const filteredData = selectedLabel
        ? chartData.filter((item) => item.label === selectedLabel)
        : chartData;

    // Totals for summary cards
    const totalQuantity = filteredData.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalRevenue = filteredData.reduce((sum, item) => sum + (item.revenue || 0), 0);

    return (
        <div className="bg-white rounded-xl shadow p-6">
            {/* Header with Title + Toggle */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-semibold text-[#003A71]">Top Drugs</h3>

                {/* Toggle Buttons */}
                <div className="flex gap-2">
                    <button
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${viewMode === "graph"
                            ? "bg-blue-500 text-white"
                            : "bg-gray-100 text-gray-600"
                            }`}
                        onClick={() => setViewMode("graph")}
                    >
                        Graph
                    </button>
                    <button
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${viewMode === "patients"
                            ? "bg-blue-500 text-white"
                            : "bg-gray-100 text-gray-600"
                            }`}
                        onClick={() => setViewMode("patients")}
                    >
                        Patient List
                    </button>
                </div>
            </div>

            <div className="flex gap-6">
                {/* Left Summary Section */}
                <div className="w-[30%] space-y-4">
                    <h3 className="text-md font-semibold text-[#003A71]">Summary</h3>

                    {/* Label Buttons */}
                    <div
                        className="flex flex-wrap gap-2 mb-2 max-h-28 overflow-y-auto"
                    >
                        {chartData.map((item) => (
                            <button
                                key={item.label}
                                className={`px-3 py-1 rounded-lg border text-sm cursor-pointer ${selectedLabel === item.label
                                        ? "border-blue-400 bg-blue-50"
                                        : "border-gray-200 bg-white hover:bg-blue-50"
                                    }`}
                                onClick={() =>
                                    setSelectedLabel(
                                        selectedLabel === item.label ? null : item.label
                                    )
                                }
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>


                    {/* Summary Cards */}
                    <div className="p-3 rounded-lg border bg-white">
                        <div className="text-sm text-gray-600">Total Quantity</div>
                        <div className="text-xl font-bold text-[#ed7d31]">{totalQuantity}</div>
                    </div>

                    <div className="p-3 rounded-lg border bg-white">
                        <div className="text-sm text-gray-600">Total Revenue</div>
                        <div className="text-xl font-bold text-[#00b0f0]">
                            ₹{totalRevenue.toFixed(2)}
                        </div>
                    </div>
                </div>

                {/* Right Chart Section */}
                <div className="w-[70%]">
                    {viewMode === "graph" ? (<ResponsiveContainer width="100%" height={260}>
                        <BarChart
                            data={filteredData}
                            margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                            barCategoryGap="20%"
                        >
                            <CartesianGrid vertical={false} stroke="#f0f0f0" />
                            <XAxis
                                dataKey="label"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#666" }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#666" }}
                            />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, fontSize: 12 }}
                                itemStyle={{ fontSize: 12 }}
                                formatter={(val, name) =>
                                    name === "Revenue"
                                        ? [`₹${val.toFixed(2)}`, name]
                                        : [val, name]
                                }
                            />
                            <Legend
                                verticalAlign="bottom"
                                height={40}
                                wrapperStyle={{
                                    fontSize: 12,
                                    paddingTop: 12,
                                    display: "flex",
                                    justifyContent: "center",
                                    lineHeight: "20px",
                                }}
                            />
                            <Bar
                                dataKey="quantity"
                                name="Quantity Sold"
                                fill="#ed7d31"
                                radius={[4, 4, 0, 0]}
                            />
                            <Bar
                                dataKey="revenue"
                                name="Revenue"
                                fill="#00b0f0"
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                    ) : (
                        // Patient List View
                        <div className="mt-4">
                            <h3 className="text-md font-semibold text-[#003A71] mb-2">
                                Patients List
                            </h3>

                            {selectedLabel ? (
                                <div className="mb-4">
                                    <div className="font-medium text-gray-700">{selectedLabel}</div>
                                    <ul className="list-disc list-inside text-sm text-gray-600 pl-4">
                                        {chartData
                                            .find((item) => item.label === selectedLabel)
                                            ?.patients?.map((p, i) => (
                                                <li className="my-4" key={i}>{p}</li>
                                            ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 italic">
                                    Please select a drug to see patient details.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>

    );
};

export default TopDrugs;
